import type { ReactNode } from "react";
import { Link } from "react-router";

import allieIcon from "../assets/allie-icon.png";

type Author = {
  name: string;
  role: string;
  avatarSrc: string;
  avatarAlt?: string;
};

const DEFAULT_AUTHOR: Author = {
  name: "Allie Aspden",
  role: "CX Product Manager, Scribenote · May 2026",
  avatarSrc: allieIcon,
  avatarAlt: "Allie Aspden",
};

export function UseCaseHero({
  title,
  deck,
  eyebrow = "Use case",
  backHref = "/",
  author = DEFAULT_AUTHOR,
}: {
  title: ReactNode;
  deck: ReactNode;
  eyebrow?: string;
  backHref?: string;
  author?: Author;
}) {
  return (
    <section className="px-[clamp(24px,5vw,80px)] pt-12 pb-14">
      <div className="mx-auto max-w-[760px]">
        <Link
          to={backHref}
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-secondary no-underline hover:text-indigo-600"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5M12 19l-7-7 7-7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back
        </Link>

        <div className="mb-6 flex flex-wrap gap-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.04em] text-indigo-700">
            {eyebrow}
          </div>
        </div>

        <h1 className="font-companion mb-6 text-[clamp(40px,5.5vw,64px)] font-bold leading-[1.05] tracking-[-0.03em] text-primary [text-wrap:balance]">
          {title}
        </h1>

        <p className="mb-9 font-companion text-[22px] leading-[1.5] text-secondary [text-wrap:pretty]">
          {deck}
        </p>

        <div className="flex items-center gap-3.5 border-t border-grindigo-100 pt-6">
          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full shadow-[0_4px_12px_-4px_rgba(99,102,241,0.5)]">
            <img
              src={author.avatarSrc}
              alt={author.avatarAlt ?? author.name}
              className="block h-full w-full object-cover"
            />
          </div>
          <div>
            <div className="text-[15px] font-semibold text-primary">
              {author.name}
            </div>
            <div className="text-[13px] text-secondary">{author.role}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
